import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { Button, TextField } from "@/components/ui";
import { api, ApiError } from "@/lib/api";
import type { MeDTO } from "@shared/types";
import { LegalCheckbox } from "@/components/Legal";
import { AuthLayout } from "./AuthLayout";

export function Signup() {
  const [form, setForm] = useState({ familyName: "", name: "", email: "", password: "" });
  const [accepted, setAccepted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const qc = useQueryClient();
  const navigate = useNavigate();
  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, [k]: e.target.value });

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const me = await api.post<MeDTO>("/auth/signup", {
        ...form,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        acceptLegal: accepted,
      });
      qc.setQueryData(["me"], me);
      navigate("/parent/setup", { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not create your account");
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthLayout title="Create your family" subtitle="You'll be the family admin. Kids get their own PIN logins next.">
      <form onSubmit={submit} className="space-y-4">
        <TextField
          label="Family name"
          placeholder="The Johnsons"
          required
          maxLength={60}
          value={form.familyName}
          onChange={set("familyName")}
        />
        <TextField label="Your name" placeholder="Alex" autoComplete="given-name" required maxLength={40} value={form.name} onChange={set("name")} />
        <TextField label="Email" type="email" autoComplete="email" required value={form.email} onChange={set("email")} />
        <TextField
          label="Password"
          type="password"
          autoComplete="new-password"
          required
          minLength={8}
          hint="At least 8 characters"
          value={form.password}
          onChange={set("password")}
        />
        <LegalCheckbox checked={accepted} onChange={setAccepted} />
        {error && <p className="text-sm font-semibold text-danger">{error}</p>}
        <Button type="submit" size="lg" block loading={busy} disabled={!accepted}>
          Create family
        </Button>
        <p className="text-center text-xs text-muted">
          We'll email you our children's privacy notice. Kids can sign in once you give consent from that email.
        </p>
      </form>
      <p className="mt-6 text-center text-sm text-muted">
        Already have an account?{" "}
        <Link to="/login" className="font-bold text-brand">
          Sign in
        </Link>
      </p>
    </AuthLayout>
  );
}
